import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { Observable, catchError, of, switchMap, take } from 'rxjs';
import { UserService } from '../services/user.service';
import { AppDialogService } from '../services/app-dialog.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { SecurityPolicies } from '../services/auth-variables';
import { TranslateService } from '@ngx-translate/core';
import { TAPErrorCodes } from '../shared/Constants/sharedConstants';
import { CsoFollowService } from '../services/cso-follow.service';

@Injectable()
export class UserDetailsGuard implements CanActivate {
  private readonly userNotInScopeMessageKey = 'Unauthorized.UserNotInScope';

  constructor(
    private _us: UserService,
    public router: Router,
    private _ads: AppDialogService,
    private authService: AuthService,
    private translate: TranslateService,
    private csoFollow: CsoFollowService,
  ) {}

  securityPolicies = SecurityPolicies;

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    let userId = Number.parseInt(route.paramMap.get('id') ?? '0');

    //check the path to see if it is my account
    const isMyAccount: boolean = state.url.toLowerCase().includes('my-account');

    return this.authService.getUserInformation().pipe(
      take(1),
      switchMap((userInfo) => {
        if (!userInfo) return of(false);

        userId = isMyAccount ? userInfo.userId : userId;

        // Folded into the chain. This used to be read synchronously out of a .subscribe()
        // callback into a local, which only worked because the user profile happens to resolve
        // from cache synchronously - the moment it did not, the guard silently denied access.
        const hasManageUsers$ = isMyAccount
          ? of(true)
          : this.authService
              .passesPolicyOf(SecurityPolicies.RequiresManageUsers)
              .pipe(take(1));

        return hasManageUsers$.pipe(
          switchMap((hasManageUsers) => {
            if (!hasManageUsers) return of(false);

            // if userId is 0, then it is a new user, so return true
            if (userId === 0) return of(true);

            const user$ = isMyAccount ? this._us.getMyAccount() : this._us.getUser(userId);

            return user$.pipe(
              take(1),
              switchMap((user) => {
                if (!user) {
                  this.handleError(new HttpErrorResponse({ status: 404 }));
                  return of(false);
                }

                // UserNotInScope arrives as HTTP 200 with a DTO carrying nothing but the code, so
                // the `!user` test above does not catch it and the guard used to allow the
                // navigation - the user page then rendered empty. handleError's UserNotInScope
                // message key could only ever fire on a 401/403, which this path never returns.
                if (user.errorCode === TAPErrorCodes.UserNotInScope) {
                  this.handleError(new HttpErrorResponse({ status: 403 }));
                  return of(false);
                }

                // Opening a user OUTSIDE the active CSO's branch moves the CSO to the organization
                // that owns them, so the rest of the app agrees with the page being shown. The read
                // gate above is the caller's own org access, which is wider than the branch, so
                // this is reachable and the switch is what makes it coherent.
                return this.csoFollow.followOrganization(
                  user.orgId,
                  user.isInCsoScope,
                );
              }),
              catchError((error: HttpErrorResponse) => {
                this.handleError(error);
                return of(false);
              }),
            );
          }),
        );
      }),
    );
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 404) {
      this._ads.showErrorMessage('User could not be found.');
    } else if (error.status === 401 || error.status === 403) {
      this._ads.showErrorMessage(
        this.translate.instant(this.userNotInScopeMessageKey),
      );
      this.router.navigate(['/dashboard']);
    } else {
      this._ads.showErrorMessage(
        'An error occurred while fetching user details.',
      );
    }
  }
}
